import { AxiosResponse } from "axios";
import authModule from "../../../utilities/AuthModule";
import { axiosRetryGet } from "../../../utilities/AxiosRetryUtil";
import { logger } from "../../../utilities/logger";
import { IManagedSite } from "../interfaces/IManagedSite";
import { buildListEndpoint, extractServerRelativeListUrl, normalizeClientListWebUrl } from "./clientListUrl";
import { CLIENT_INACTIVE_KEYS, CLIENT_LIST_REQUEST_TIMEOUT_MS, CLIENT_SITE_ID_KEYS, CLIENT_SITE_URL_KEYS } from "./clientTypeQuery";
import { IClientListContext, isClientListUnreadable, resolveClientListContext } from "./clientTypeService";

// What a read of an unreadable list fails with: the schema read was refused and logged already,
// so its rows are not asked for and the failure is not logged again (production log 2026-10-26).
const CLIENT_LIST_UNREADABLE: Error = new Error("the client list cannot be read by this user, or is not there");

// The client list is a global inactive-site indicator source, independent of managed sites. We
// read only the site url/id of rows flagged inactive (the "Inactief" field) and return them for
// correlation. The url logic lives in ./clientListUrl so it stays unit-testable and in step with
// FortesTemplatePlaceholders.cs, which resolves the very same configured value server-side. The
// column names come from the schema read every client-list feature shares, once per session.
// rethrow: reject on a failed read instead of answering the (partial or empty) list, so a caller
// that excludes inactive clients can tell a failure from "none" (Copilot; audit 2026-10-19).
// onComplete: told whether the read was complete, for a caller that keeps the (partial) list
// (review 2026-10-20: SharePointSiteHelper.inactiveClientSitesComplete).
export const getInactiveClientSites = async (clientListUrl: string, options: { rethrow?: boolean; onComplete?: (complete: boolean) => void } = {}): Promise<IManagedSite[]> => {
  const inactiveSites: IManagedSite[] = [];

  const webUrl: string = normalizeClientListWebUrl(clientListUrl);
  if (!webUrl) {
    options.onComplete?.(true);
    return inactiveSites;
  }

  const isTruthyInactief = (value: any): boolean =>
    value === true || value === 1 || ["true", "1", "yes", "ja"].includes(String(value ?? "").trim().toLowerCase());

  try {
    // Undefined when the schema could not be read: every row is then read, and its columns are
    // matched by name below - unless the list cannot be read at all.
    const context: IClientListContext | undefined = await resolveClientListContext(clientListUrl);
    if (!context && isClientListUnreadable(clientListUrl)) {
      throw CLIENT_LIST_UNREADABLE;
    }
    const accessToken: string = await authModule.getSharePointAccessToken();

    const listEndpoint: string = context?.listEndpoint ?? buildListEndpoint(extractServerRelativeListUrl(clientListUrl));
    const inactiefInternal: string | undefined = context?.columns?.inactiveInternal;
    const siteUrlInternal: string | undefined = context?.columns?.siteUrlInternal;
    const siteIdInternal: string | undefined = context?.columns?.siteIdInternal;

    const fetchItems = async (useServerFilter: boolean): Promise<any[]> => {
      const items: any[] = [];
      const selectClause: string = siteUrlInternal && siteIdInternal && inactiefInternal
        ? `&$select=${siteUrlInternal},${siteIdInternal},${inactiefInternal}`
        : "";
      // Inactief is guaranteed indexed, so this filter is threshold-safe for the inactive subset.
      const filterClause: string = useServerFilter && inactiefInternal ? `&$filter=${inactiefInternal} eq 1` : "";
      let nextLink: string = `${webUrl}/_api/web/${listEndpoint}/items?$top=5000${selectClause}${filterClause}`;
      while (nextLink) {
        // Bounded: the site list load at session start waits for this read.
        const requestConfig = {
          headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json;odata=nometadata" },
          timeout: CLIENT_LIST_REQUEST_TIMEOUT_MS,
        };
        const response: AxiosResponse<any> = await axiosRetryGet(nextLink, requestConfig);
        const pageItems: any[] = response?.data?.value?.filter(item => item);
        if (Array.isArray(pageItems) && pageItems.length > 0) {
          for (const pageItem of pageItems) {
            items.push(pageItem);
          }
        }
        nextLink = response?.data
          ? response.data["odata.nextLink"]
          : undefined;
      }
      return items;
    };

    const useServerFilter: boolean = !!inactiefInternal;

    let items: any[];
    try {
      // Primary path: server-side filter on the indexed Inactief column.
      items = await fetchItems(useServerFilter);
    } catch (filterError) {
      // Retry unfiltered only when the failed attempt actually used the server filter; an
      // unfiltered failure would just fail identically again. The fallback covers the residual
      // edges: an inactive subset that itself exceeds the list view threshold, or a non-boolean
      // Inactief type where "eq 1" does not match (the client-side truthy check below filters).
      if (!useServerFilter) {
        throw filterError;
      }
      logger(filterError, "inactiveClientSites.ts (getInactiveClientSites - server filter fallback)");
      items = await fetchItems(false);
    }

    for (const item of items) {
      const itemProperties: string[] = Object.keys(item);
      const inactiefPropKey: string | undefined = (inactiefInternal && itemProperties.find(key => key.toLowerCase().trim() === inactiefInternal.toLowerCase().trim()))
        || itemProperties.find(key => CLIENT_INACTIVE_KEYS.some(inactiefKey => key.toLowerCase().trim() === inactiefKey));

      // Keep only inactive rows. This is the primary filter when the server-side filter was not
      // applied, and a harmless re-assertion when it was.
      if (!inactiefPropKey || !isTruthyInactief(item[inactiefPropKey])) {
        continue;
      }

      const siteUrlPropKey: string | undefined = (siteUrlInternal && itemProperties.find(key => key.toLowerCase().trim() === siteUrlInternal.toLowerCase().trim()))
        || itemProperties.find(key => CLIENT_SITE_URL_KEYS.some(siteUrlKey => key.toLowerCase().trim() === siteUrlKey));
      const siteIdPropKey: string | undefined = (siteIdInternal && itemProperties.find(key => key.toLowerCase().trim() === siteIdInternal.toLowerCase().trim()))
        || itemProperties.find(key => CLIENT_SITE_ID_KEYS.some(siteIdKey => key.toLowerCase().trim() === siteIdKey));

      if (siteUrlPropKey || siteIdPropKey) {
        inactiveSites.push({
          siteGroup: undefined,
          siteUrl: siteUrlPropKey ? (item[siteUrlPropKey]?.Url || item[siteUrlPropKey])?.toLowerCase()?.trim() : undefined,
          siteId: siteIdPropKey ? item[siteIdPropKey]?.toLowerCase()?.trim() : undefined
        });
      }
    }
    options.onComplete?.(true);
  } catch (err) {
    if (err !== CLIENT_LIST_UNREADABLE) {
      logger(err, "inactiveClientSites.ts (getInactiveClientSites)");
    }
    options.onComplete?.(false);
    if (options.rethrow) {
      throw err;
    }
  }

  return inactiveSites;
};
